import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Lock, LogOut, RefreshCw, Search, Users } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "aria_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q));
  }, [rows, query]);

  const loadRows = async (authToken) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/waitlist`, {
        headers: { "X-Admin-Token": authToken },
      });
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Failed to load waitlist.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadRows(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, password);
      setToken(password);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/waitlist.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      a.download = `aria-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(rows.map((r) => r.email).join("\n"));
      toast.success(`Copied ${rows.length} emails.`);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <main
        data-testid="admin-login"
        className="flex min-h-screen items-center justify-center bg-[#161616] px-6 text-[#ededee]"
      >
        <form
          onSubmit={handleLogin}
          className="aria-rise w-full max-w-sm rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7 backdrop-blur-md"
        >
          <div className="mb-6 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03]">
              <Lock className="h-4 w-4 text-white/70" />
            </div>
            <div>
              <p className="text-[15px] font-medium text-white">ARIA · Admin</p>
              <p className="text-[12px] text-white/45">Restricted area</p>
            </div>
          </div>
          <label className="mb-1.5 block text-[12px] tracking-wide text-white/55">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            data-testid="admin-password-input"
            className="aria-input mb-5 block h-11 w-full rounded-xl px-4 text-[14px]"
            placeholder="••••••••••"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="aria-pill inline-flex h-11 w-full items-center justify-center text-[14px] font-medium text-white disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#161616] text-[#ededee]"
    >
      <header className="border-b border-white/[0.05] px-6 py-5 sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/aria-logo.png"
              alt="ARIA"
              className="h-8 w-auto"
              draggable={false}
            />
            <div className="hidden sm:block">
              <p className="text-[14px] font-medium text-white">ARIA · Admin</p>
              <p className="text-[11px] text-white/45">Waitlist</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1.5 text-[12px] text-white/45 transition-colors hover:text-white/80"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/45">
              <Users className="h-3.5 w-3.5" /> Total signups
            </div>
            <div
              className="text-3xl font-semibold tracking-tight text-white"
              data-testid="admin-total-count"
            >
              {rows.length}
            </div>
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-white/45">
              Latest
            </div>
            <div className="truncate text-[15px] text-white">
              {rows.length ? rows[rows.length - 1].email : "—"}
            </div>
            <div className="mt-0.5 text-[12px] text-white/40">
              {rows.length ? fmt(rows[rows.length - 1].joined_at) : ""}
            </div>
          </div>
          <div className="col-span-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 sm:col-span-1">
            <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-white/45">
              Actions
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => loadRows(token)}
                disabled={loading}
                data-testid="admin-refresh"
                className="aria-pill inline-flex h-9 items-center gap-1.5 px-3 text-[12px] text-white"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              <button
                onClick={handleDownloadCsv}
                disabled={!rows.length}
                data-testid="admin-download-csv"
                className="aria-pill inline-flex h-9 items-center gap-1.5 px-3 text-[12px] text-white disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
              <button
                onClick={handleCopyAll}
                disabled={!rows.length}
                className="aria-pill inline-flex h-9 items-center gap-1.5 px-3 text-[12px] text-white disabled:opacity-50"
              >
                Copy emails
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="aria-input mb-4 flex h-11 items-center gap-2 rounded-full px-4">
          <Search className="h-4 w-4 text-white/35" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search emails…"
            data-testid="admin-search"
            className="h-full flex-1 bg-transparent text-[14px] outline-none"
          />
          {query ? (
            <button
              onClick={() => setQuery("")}
              className="text-[12px] text-white/40 hover:text-white/70"
            >
              Clear
            </button>
          ) : null}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
          <div className="grid grid-cols-[40px_1fr_180px] gap-4 border-b border-white/[0.05] px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
            <div>#</div>
            <div>Email</div>
            <div className="text-right">Joined</div>
          </div>
          {loading && rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-[13px] text-white/40">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="px-5 py-12 text-center text-[13px] text-white/40"
            >
              {rows.length === 0
                ? "No signups yet."
                : "No matches for that search."}
            </div>
          ) : (
            <ul data-testid="admin-rows">
              {filtered.map((r, i) => {
                const idx = rows.indexOf(r) + 1;
                return (
                  <li
                    key={r.id}
                    className="grid grid-cols-[40px_1fr_180px] items-center gap-4 border-b border-white/[0.04] px-5 py-3 text-[14px] last:border-b-0 hover:bg-white/[0.015]"
                  >
                    <div className="text-[12px] text-white/35">{idx}</div>
                    <div className="truncate text-white">{r.email}</div>
                    <div className="text-right text-[12px] text-white/45">
                      {fmt(r.joined_at)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-6 text-[11px] text-white/30">
          Data is stored in MongoDB ({"`waitlist`"} collection) with a unique
          index on email. CSV exports include all stored fields.
        </p>
      </section>
    </main>
  );
}
